import { Card, Stack, Typography } from '@mui/material';
import { useMemo } from 'react';
import {
    CartesianGrid,
    Line,
    LineChart,
    ReferenceDot,
    ReferenceLine,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts';
import useUnitProvider from '/@/core/context/unit';
import { _ } from '/@/locale';
import { planTicks } from './axis-ticks';
import type { ManagementHybrid } from './use-management-data';

type ChartRow = { density: number } & Record<string, number>;

const AXIS_COLOR = 'rgba(16,56,79,0.12)';
const Y_AXIS_WIDTH = 56;

/**
 * How many ticks each axis aims for. The density labels are short ("3", "4", …) so its axis
 * takes more of them; the yield labels are five characters wide and need the room.
 */
const DENSITY_TICK_TARGET = 10;
const YIELD_TICK_TARGET = 5;

/**
 * Precision for the conversions the ticks are planned from. The converters round to the
 * display precision by default (0 or 1 decimals), which is right for a label but would move
 * the domain the interval is derived from, so the planning asks for the unrounded value.
 */
const PLANNING_PRECISION = 6;

/**
 * Merges the per-hybrid curves onto a shared density axis so one hover reads every hybrid's
 * yield at that density, which is what the tooltip in the design shows. Densities come back
 * on the same grid for every product, so rows line up by value.
 */
const toChartRows = (hybrids: ManagementHybrid[]): ChartRow[] => {
    const byDensity = new Map<number, ChartRow>();
    hybrids.forEach((hybrid) => {
        hybrid.points.forEach((point) => {
            const row = byDensity.get(point.density) || ({ density: point.density } as ChartRow);
            row[String(hybrid.id)] = point.yield;
            byDensity.set(point.density, row);
        });
    });
    return [...byDensity.values()].sort((a, b) => a.density - b.density);
};

/** Union of every hybrid's yields — the domain the shared Y axis has to cover. */
const yieldExtent = (hybrids: ManagementHybrid[]): [number, number] | undefined => {
    const values = hybrids.flatMap((hybrid) => hybrid.points.map((point) => point.yield));
    if (!values.length) return undefined;
    return [Math.min(...values), Math.max(...values)];
};

function DensityTooltip({
    active,
    payload,
    label,
    hybrids,
    formatDensity,
    formatYield,
}: {
    active?: boolean;
    payload?: { dataKey?: string | number; value?: number }[];
    label?: number;
    hybrids: ManagementHybrid[];
    formatDensity: (value: number) => string;
    formatYield: (value: number) => string;
}) {
    if (!active || !payload?.length || label === undefined) return null;

    return (
        <Card elevation={3} sx={{ p: 1.5, borderRadius: '4px' }}>
            <Stack spacing={0.5}>
                <Typography variant='caption' fontWeight={700} color='primary'>
                    {_('Densidad: {{density}}', { density: formatDensity(label) })}
                </Typography>
                {payload.map((entry) => {
                    const hybrid = hybrids.find(({ id }) => String(id) === String(entry.dataKey));
                    if (!hybrid || entry.value === undefined) return null;
                    return (
                        <Stack key={hybrid.id} direction='row' spacing={0.5} alignItems='center'>
                            <Dot color={hybrid.color} />
                            <Typography variant='caption' color='primary'>
                                {`${hybrid.catalogName}: ${formatYield(entry.value)}`}
                            </Typography>
                        </Stack>
                    );
                })}
            </Stack>
        </Card>
    );
}

/** Lets the chart size itself from the viewport, or from an explicit width when there is none. */
export function ChartFrame({
    width,
    height,
    children,
}: {
    width?: number;
    height: number;
    children: React.ReactElement;
}) {
    if (width) return children;
    return (
        <ResponsiveContainer width='100%' height={height}>
            {children}
        </ResponsiveContainer>
    );
}

function Dot({ color }: { color: string }) {
    return <Stack sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: color, flexShrink: 0 }} />;
}

/** "Curva de densidad": estimated yield against harvest density, one line per hybrid. */
function DensityCurve({
    hybrids,
    height = 320,
    width,
}: {
    hybrids: ManagementHybrid[];
    height?: number;
    /**
     * Fixed chart width. Only the PDF passes it: `ResponsiveContainer` sizes itself by
     * measuring the DOM, and server-side rendering has no layout to measure, so the chart
     * would come out empty.
     */
    width?: number;
}) {
    const units = useUnitProvider();
    // The curve is ~180 points per hybrid; merging them is not work to redo on every render.
    const rows = useMemo(() => toChartRows(hybrids), [hybrids]);

    // Precision is left to the converter, which adapts to the user's density/yield units.
    // The tooltip carries units; the axes don't, since their titles already state them.
    const formatDensity = (value: number) => units.toCornDensity(value, undefined).toString();
    const formatYield = (value: number) => units.toCornYield(value, undefined).toString();
    const formatDensityTick = (value: number) => units.toCornDensity(value, undefined).localeValue;
    const formatYieldTick = (value: number) => units.toCornYield(value, undefined).localeValue;

    /**
     * Ticks are planned in the user's units so the labels land on round values, then mapped
     * back to the raw values the axes are plotted in. `fromYield` throws for a session with
     * no yield unit configured (`toYield` falls back, its inverse doesn't), and a plan is
     * optional anyway — either way the axis falls back to recharts' own ticks.
     */
    const axes = useMemo(() => {
        const densities = rows.map((row) => row.density);
        const yields = yieldExtent(hybrids);
        if (!densities.length || !yields) return {};
        try {
            return {
                density: planTicks({
                    min: Math.min(...densities),
                    max: Math.max(...densities),
                    toDisplay: (value) => units.toCornDensity(value, PLANNING_PRECISION).value as number,
                    fromDisplay: (value) => units.fromCornDensity(value),
                    targetCount: DENSITY_TICK_TARGET,
                }),
                yield: planTicks({
                    min: yields[0],
                    max: yields[1],
                    toDisplay: (value) => units.toCornYield(value, PLANNING_PRECISION).value as number,
                    fromDisplay: (value) => units.fromYield(value, 'corn'),
                    targetCount: YIELD_TICK_TARGET,
                    // The top and bottom labels sit on the axis ends, as in the design.
                    extendDomain: true,
                }),
            };
        } catch {
            return {};
        }
    }, [rows, hybrids, units]);

    if (!rows.length) return null;

    return (
        <Card elevation={0} variant='outlined' sx={{ borderRadius: '8px', p: 2 }}>
            <Stack spacing={2}>
                <Stack>
                    <Typography variant='subtitle1' fontWeight={700} color='primary'>
                        {_('Curva de densidad')}
                    </Typography>
                </Stack>

                <Stack>
                    <Typography variant='caption' color='primary.mui.inactive'>
                        {_('Rendimiento estimado')} ({units.getUnit('yield', 'corn')})
                    </Typography>
                    <ChartFrame width={width} height={height}>
                        <LineChart
                            data={rows}
                            width={width}
                            height={height}
                            margin={{ top: 8, right: 16, bottom: 8, left: 8 }}
                        >
                            <CartesianGrid stroke={AXIS_COLOR} vertical={false} />
                            <XAxis
                                dataKey='density'
                                type='number'
                                domain={axes.density?.domain || ['dataMin', 'dataMax']}
                                ticks={axes.density?.ticks}
                                tickFormatter={formatDensityTick}
                                tickLine={false}
                                axisLine={{ stroke: AXIS_COLOR }}
                                height={24}
                            />
                            {/*
                             * One shared Y axis for every hybrid: each <Line> binds to the default
                             * axis, and recharts unions their domains — so "multiple yields" needs
                             * no extra axes. `density` sits on the X axis and is excluded here.
                             */}
                            <YAxis
                                type='number'
                                domain={axes.yield?.domain || ['dataMin', 'auto']}
                                ticks={axes.yield?.ticks}
                                tickFormatter={formatYieldTick}
                                tickLine={false}
                                axisLine={{ stroke: AXIS_COLOR }}
                                width={Y_AXIS_WIDTH}
                            />

                            <Tooltip
                                content={
                                    <DensityTooltip
                                        hybrids={hybrids}
                                        formatDensity={formatDensity}
                                        formatYield={formatYield}
                                    />
                                }
                            />
                            {/*
                             * Each hybrid's recommended density, dropped as a dashed vertical
                             * line in its own colour. `ifOverflow='hidden'` keeps a stray value
                             * from stretching the axis domain.
                             */}
                            {hybrids.map((hybrid) =>
                                hybrid.harvestDensity ? (
                                    <ReferenceLine
                                        key={`recommended-${hybrid.id}`}
                                        stroke={hybrid.color}
                                        strokeDasharray='4 4'
                                        ifOverflow='hidden'
                                        segment={[
                                            { x: hybrid.harvestDensity, y: 0 },
                                            { x: hybrid.harvestDensity, y: hybrid.estimatedYield },
                                        ]}
                                    />
                                ) : null
                            )}
                            {/* Marks where each recommended density meets its curve. */}
                            {hybrids.map((hybrid) =>
                                hybrid.harvestDensity && hybrid.estimatedYield ? (
                                    <ReferenceDot
                                        key={`recommended-dot-${hybrid.id}`}
                                        x={hybrid.harvestDensity}
                                        y={hybrid.estimatedYield}
                                        r={4}
                                        fill={hybrid.color}
                                        stroke={hybrid.color}
                                        ifOverflow='hidden'
                                    />
                                ) : null
                            )}
                            {hybrids.map((hybrid) => (
                                <Line
                                    key={hybrid.id}
                                    type='monotone'
                                    dataKey={String(hybrid.id)}
                                    stroke={hybrid.color}
                                    strokeWidth={2}
                                    dot={false}
                                    activeDot={{ r: 4 }}
                                    isAnimationActive={false}
                                />
                            ))}
                        </LineChart>
                    </ChartFrame>

                    <Typography variant='caption' color='primary.mui.inactive' textAlign='right'>
                        {_('Densidad')} ({units.getUnit('density', 'corn')})
                    </Typography>
                </Stack>

                <Stack direction='row' spacing={2} flexWrap='wrap' useFlexGap alignItems='center'>
                    <Typography variant='caption' color='primary.mui.inactive'>
                        {_('Híbridos seleccionados')}
                    </Typography>
                    {hybrids.map((hybrid) => (
                        <Stack key={hybrid.id} direction='row' spacing={0.5} alignItems='center'>
                            <Dot color={hybrid.color} />
                            <Typography variant='caption' color='primary'>
                                {hybrid.catalogName}
                            </Typography>
                        </Stack>
                    ))}
                </Stack>
            </Stack>
        </Card>
    );
}

export default DensityCurve;
